// File: app/src/audio/instruments.js
//
// Hand-built instruments for the sound sets a plain synth can't play: a struck
// bell, a referee's whistle, a bugle, woodblocks, a hand drum, a marimba
// and a hand clap. Each one schedules a single note at `t` into `out` (a native
// AudioNode) using plain Web Audio nodes, which the browser collects once they
// stop, so there is nothing to dispose.
//
// No imports on purpose: the loudness harness loads this file in a bare page.
//
// Signature: (ctx, out, t, freq, duration, options) where `options` merges the
// voice's options with the note's own (vel, damp, roll, bend).

const SILENT = 0.0001;
const noiseBuffers = new WeakMap();

function noiseBuffer(ctx) {
  if (!noiseBuffers.has(ctx)) {
    const buffer = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    noiseBuffers.set(ctx, buffer);
  }
  return noiseBuffers.get(ctx);
}

// A gain that rises to `peak` over `attack`, holds for `hold`, then decays
// exponentially over `decay`. With `cutAt`, the sound is damped there instead.
function envelope(ctx, out, t, { peak: level = 1, attack = 0.002, hold = 0, decay = 0.2, cutAt } = {}) {
  // Exponential ramps can't start from or reach zero.
  const peak = Math.max(level, SILENT * 2);
  const g = ctx.createGain();
  const decayStart = t + attack + hold;
  const end = decayStart + decay;
  g.gain.setValueAtTime(SILENT, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  if (hold) g.gain.setValueAtTime(peak, decayStart);
  if (cutAt !== undefined && cutAt < end) {
    // Where the decay would have reached at cutAt, then a quick fade.
    const at = Math.max(cutAt, decayStart);
    const level = peak * (SILENT / peak) ** ((at - decayStart) / decay);
    g.gain.exponentialRampToValueAtTime(Math.max(level, SILENT), at);
    g.gain.exponentialRampToValueAtTime(SILENT, at + 0.04);
    g.connect(out);
    return { gain: g, end: at + 0.05 };
  }
  g.gain.exponentialRampToValueAtTime(SILENT, end);
  g.connect(out);
  return { gain: g, end };
}

function oscillator(ctx, type, freq, t, end) {
  const o = ctx.createOscillator();
  o.type = type;
  o.frequency.setValueAtTime(freq, t);
  o.start(t);
  o.stop(end + 0.02);
  return o;
}

function partial(ctx, out, t, freq, { type = "sine", peak, attack, hold, decay, cutAt }) {
  const env = envelope(ctx, out, t, { peak, attack, hold, decay, cutAt });
  const o = oscillator(ctx, type, freq, t, env.end);
  o.connect(env.gain);
  return o;
}

function noise(ctx, out, t, { filter = "bandpass", freq = 2000, Q = 1, peak = 1, attack = 0.001, hold = 0, decay = 0.03 }) {
  const env = envelope(ctx, out, t, { peak, attack, hold, decay });
  const f = ctx.createBiquadFilter();
  f.type = filter;
  f.frequency.setValueAtTime(freq, t);
  f.Q.setValueAtTime(Q, t);
  f.connect(env.gain);
  const src = ctx.createBufferSource();
  src.buffer = noiseBuffer(ctx);
  src.loop = true;
  src.connect(f);
  src.start(t);
  src.stop(env.end + 0.02);
}

// A struck bell: inharmonic partials, each [ratio, level, share of the ring
// time], plus a short metallic "tink". `damp` cuts the ring at the end of the
// note; `roll` strikes it repeatedly for the note's length, like an electric
// ring bell, then damps it.
function bell(ctx, out, t, freq, duration, { partials, ring = 2.2, vel = 1, damp = false, roll }) {
  if (roll) {
    const cutAt = t + duration;
    for (let s = t; s < cutAt - 0.03; s += roll) strike(s, vel * 0.3, cutAt);
    return;
  }
  strike(t, vel, damp ? t + duration : undefined);

  function strike(at, level, cutAt) {
    for (const [ratio, amp, share] of partials) {
      partial(ctx, out, at, freq * ratio, { peak: amp * level, attack: 0.001, decay: ring * share, cutAt });
    }
    noise(ctx, out, at, { filter: "highpass", freq: 4000, decay: 0.015, peak: 0.35 * level });
  }
}

// Woodblock, claves or a wooden clack: a short tone, a fainter upper partial
// and a click of noise.
function wood(ctx, out, t, freq, duration, { vel = 1, decay = 0.13, upper = 2.7, upperLevel = 0.4, upperDecay = 0.05, click = 0.4 }) {
  partial(ctx, out, t, freq, { peak: vel, decay });
  if (upperLevel > 0) partial(ctx, out, t, freq * upper, { peak: upperLevel * vel, decay: upperDecay });
  noise(ctx, out, t, { freq: freq * 1.5, Q: 4, decay: 0.012, peak: click * vel });
}

// A hand drum: a pitch that drops as the skin settles, plus the slap of the
// hand, which is what carries on a phone speaker.
function drum(ctx, out, t, freq, duration, { vel = 1 }) {
  const env = envelope(ctx, out, t, { peak: 0.45 * vel, attack: 0.001, decay: 0.3 });
  const o = oscillator(ctx, "sine", freq * 1.8, t, env.end);
  o.frequency.exponentialRampToValueAtTime(freq, t + 0.08);
  o.connect(env.gain);
  noise(ctx, out, t, { freq: 1200, Q: 1, decay: 0.04, peak: 0.6 * vel });
}

// A marimba bar: the fundamental, the bar's bright fourth partial that dies
// away fast, and a faint knock of the mallet.
function mallet(ctx, out, t, freq, duration, { vel = 1 }) {
  partial(ctx, out, t, freq, { peak: vel, decay: 0.5 });
  partial(ctx, out, t, freq * 4, { peak: 0.25 * vel, decay: 0.07 });
  partial(ctx, out, t, freq * 10, { peak: 0.08 * vel, decay: 0.015 });
}

// A referee's pea whistle: a high tone wobbled fast by the rattling pea, with
// breath noise around it. `bend` slides the pitch over the blast.
function whistle(ctx, out, t, freq, duration, { vel = 1, bend = 1, trillRate = 32, trillCents = 80 }) {
  const env = envelope(ctx, out, t, { peak: 0.8 * vel, attack: 0.015, hold: duration, decay: 0.05 });
  const o = oscillator(ctx, "sine", freq, t, env.end);
  o.frequency.linearRampToValueAtTime(freq * bend, t + duration);
  const lfo = oscillator(ctx, "sine", trillRate, t, env.end);
  const depth = ctx.createGain();
  depth.gain.setValueAtTime(trillCents, t);
  lfo.connect(depth);
  depth.connect(o.detune);
  o.connect(env.gain);
  noise(ctx, out, t, { freq, Q: 6, attack: 0.01, hold: duration, decay: 0.05, peak: 0.5 * vel });
}

// A bugle: one sawtooth through a lowpass that opens as the note speaks
// (brass gets brighter as it gets louder), a slight scoop up into pitch, a
// puff of breath, and vibrato that creeps in on held notes. Notes are played
// a touch short of their step so phrases are tongued, not slurred.
function bugle(ctx, out, t, freq, duration, { vel = 1 }) {
  const len = Math.max(0.04, duration - 0.02);
  const env = envelope(ctx, out, t, { peak: vel, attack: 0.03, hold: Math.max(0, len - 0.03), decay: 0.06 });
  const highpass = ctx.createBiquadFilter();
  highpass.type = "highpass";
  highpass.frequency.setValueAtTime(200, t);
  highpass.connect(env.gain);
  const bell = ctx.createBiquadFilter();
  bell.type = "peaking";
  bell.frequency.setValueAtTime(1400, t);
  bell.gain.setValueAtTime(5, t);
  bell.connect(highpass);
  const lips = ctx.createBiquadFilter();
  lips.type = "lowpass";
  lips.Q.setValueAtTime(1.5, t);
  lips.frequency.setValueAtTime(freq * 1.2, t);
  lips.frequency.exponentialRampToValueAtTime(freq * 7, t + 0.05);
  lips.frequency.exponentialRampToValueAtTime(freq * 4.5, t + 0.2);
  lips.connect(bell);
  const o = oscillator(ctx, "sawtooth", freq * 0.97, t, env.end);
  o.frequency.exponentialRampToValueAtTime(freq, t + 0.04);
  if (len > 0.3) {
    const vibrato = oscillator(ctx, "sine", 5.5, t + 0.2, env.end);
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(0, t);
    depth.gain.linearRampToValueAtTime(14, t + 0.5);
    vibrato.connect(depth);
    depth.connect(o.detune);
  }
  o.connect(lips);
  noise(ctx, out, t, { freq: freq * 3, Q: 2, decay: 0.05, peak: 0.05 * vel });
}

// A hand clap: three quick bursts of noise as the palms meet, then the
// longer smack that carries.
function clap(ctx, out, t, freq, duration, { vel = 1 }) {
  [0, 0.008].forEach((d) => noise(ctx, out, t + d, { freq, Q: 1.2, decay: 0.008, peak: 0.5 * vel }));
  noise(ctx, out, t + 0.017, { freq, Q: 1.2, attack: 0.002, hold: 0.012, decay: 0.12, peak: 0.6 * vel });
}

export const INSTRUMENTS = { bell, wood, drum, mallet, whistle, bugle, clap };
